/**
 * EyedropperHandler — mounts inside the R3F Canvas and manages the active
 * eyedropper pick session when a Select / SelectSearch field arms pick mode.
 *
 * Responsibilities while a session is active:
 *   • Changes the canvas cursor to "crosshair" so the user knows pick mode
 *     is live, and restores it when the session ends.
 *   • Listens for Escape on `window` and cancels the session cleanly.
 *
 * Object taps are intercepted in `useObjectTap` (which calls `onPick` and
 * ends the session). Background clicks (pointer misses) are handled by the
 * `onPointerMissed` prop on <Canvas> in Viewport3D.
 */

import { useThree } from "@react-three/fiber";
import { useEffect } from "react";
import { useStore } from "../app/store.ts";

export function EyedropperHandler() {
  const { gl } = useThree();
  const session = useStore((s) => s.eyedropperSession);

  useEffect(() => {
    if (!session) return;

    const canvas = gl.domElement;
    canvas.style.cursor = "crosshair";

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      const current = useStore.getState().eyedropperSession;
      if (!current) return;
      e.preventDefault();
      current.onCancel();
      useStore.getState().endEyedropperSession();
    }

    window.addEventListener("keydown", onKeyDown);

    return () => {
      canvas.style.cursor = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [session, gl]);

  return null;
}
